import React, { useCallback, useEffect, useState } from 'react'
import FormTile from 'src/components/common/containers/FormTile'
import RewardConfigTile from 'src/components/rewards/setup/RewardConfigTile'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import BurnRuleDialog from 'src/components/rewards/setup/burn/dialogs/BurnRuleDialog'
import { BurnConfigType, BurnConditionsConfigType } from 'src/lib/types/rewards'
import {
  getBurnRewardsPayload,
  generateBurnRuleDialogFooterText,
  getBurnRewardsConditionPayload,
  generateMinOVDialogFooterText
} from 'src/lib/utils/rewards'
import { isEmptyObj } from 'src/lib/utils/helpers'
import MinOrderValueDialog from 'src/components/rewards/setup/MinOrderValueDialog'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { useRewardsPermissions } from 'src/lib/access-permission-hooks/useRewardsPermissions'

interface BurnRewardsProps {
  handleUpdateRewardConfig: (payload: any) => Promise<void>
  rewardsConfig: any
}

const BurnRewards: React.FC<BurnRewardsProps> = ({ handleUpdateRewardConfig, rewardsConfig }) => {
  const { sendActivityLog } = useActivityLogs()
  const { canEditSetupRewards } = useRewardsPermissions()
  const [initialBurnConfig, setInitialBurnConfig] = useState<BurnConfigType>({
    burnRatio: 100
  })
  const [initialBurnConditionsConfig, setInitialBurnConditionsConfig] = useState<BurnConditionsConfigType>({
    min_ov: 0
  })
  const [openRuleDialog, setOpenRuleDialog] = useState<boolean>(false)
  const [openMinOVDialog, setOpenMinOVDialog] = useState<boolean>(false)
  const [burnConfig, setBurnConfig] = useState<BurnConfigType>({ burnRatio: 100 })
  const [burnConditionsConfig, setBurnConditionsConfig] = useState<BurnConditionsConfigType>({
    min_ov: 0
  })

  useEffect(() => {
    if (!rewardsConfig || isEmptyObj(rewardsConfig)) return

    const initialConfig: BurnConfigType = {
      burnRatio: (rewardsConfig as any).event_reward_rule_map?.REDEMPTION?.amount * 100
    }
    const initialConditionsConfig: BurnConditionsConfigType = {
      min_ov:
        (rewardsConfig as any).event_reward_rule_map?.REDEMPTION?.blocking_conditions?.find(
          (condition: any) => condition.type === 'MIN_CART_VALUE'
        )?.value ?? 0
    }

    setInitialBurnConfig({ ...initialConfig })
    setInitialBurnConditionsConfig({ ...initialConditionsConfig })
    setBurnConfig({ ...initialConfig })
    setBurnConditionsConfig({ ...initialConditionsConfig })
  }, [rewardsConfig])

  const handleBurnRewardsSave = useCallback(async () => {
    const payload = getBurnRewardsPayload(burnConfig)
    try {
      await handleUpdateRewardConfig(payload)
      const activityLogPayload = {
        entityName: EntityName.REWARDS,
        actionName: 'edited the configuration'
      }
      sendActivityLog(activityLogPayload)
    } catch (e) {
      console.error(e)
    } finally {
      setOpenRuleDialog(false)
    }
  }, [burnConfig])

  const handleBurnConditionsSave = useCallback(
    async (conditionType: keyof BurnConditionsConfigType) => {
      const payload = getBurnRewardsConditionPayload(burnConditionsConfig, conditionType)
      try {
        await handleUpdateRewardConfig(payload)
        const activityLogPayload = {
          entityName: EntityName.REWARDS,
          actionName: 'edited the configuration'
        }
        sendActivityLog(activityLogPayload)
      } catch (e) {
        console.error(e)
      } finally {
        if (conditionType === 'min_ov') setOpenMinOVDialog(false)
      }
    },
    [burnConditionsConfig]
  )

  return (
    <>
      <FormTile title={'Burn during purchase'} tooltip={''}>
        <>
          <ul className='flex flex-col rounded-lg border-1 divide-y-1 border-gray-light divide-gray-light'>
            <li className='rounded-t-[inherit]' onClick={() => setOpenRuleDialog(canEditSetupRewards)}>
              <RewardConfigTile
                header={<>{parseFloat(initialBurnConfig.burnRatio?.toString() ?? '0').toFixed(2)}%</>}
                subHeader={<>Burn allowed of order total</>}
              />
            </li>
            <li onClick={() => setOpenMinOVDialog(canEditSetupRewards)} className='rounded-b-[inherit]'>
              <RewardConfigTile
                header={<>₹{initialBurnConditionsConfig.min_ov}</>}
                subHeader={<>Minimum order value for burn</>}
              />
            </li>
          </ul>
        </>
      </FormTile>

      {Boolean(openRuleDialog) && (
        <FloGenericDialog
          width={800}
          isOpen={openRuleDialog}
          handleClose={() => setOpenRuleDialog(false)}
          content={
            <BurnRuleDialog
              setBurnConfig={values => setBurnConfig(oldValues => ({ ...oldValues, ...values }))}
              burnConfig={burnConfig}
              initialBurnConfig={initialBurnConfig}
            />
          }
          referrer='burn-change-rule'
          title={'Set burn limit'}
          jobButtonText={'Save'}
          handleJob={async () => {
            handleBurnRewardsSave()
          }}
          footerTxt={generateBurnRuleDialogFooterText(burnConfig)}
        />
      )}

      {Boolean(openMinOVDialog) && (
        <FloGenericDialog
          width={800}
          isOpen={openMinOVDialog}
          handleClose={() => setOpenMinOVDialog(false)}
          content={
            <MinOrderValueDialog
              setMinOV={(value: number) => {
                setBurnConditionsConfig((oldValues: BurnConditionsConfigType) => ({ ...oldValues, min_ov: value }))
              }}
              minOV={burnConditionsConfig.min_ov}
              txnType='BURN'
              initialMinOv={initialBurnConditionsConfig.min_ov ?? 0}
            />
          }
          referrer='burn-conditions-min_ov-rule'
          title={'Set minimum order value for burn'}
          jobButtonText={'Save'}
          handleJob={async () => {
            handleBurnConditionsSave('min_ov')
          }}
          footerTxt={generateMinOVDialogFooterText(burnConditionsConfig.min_ov, 'BURN')}
        />
      )}
    </>
  )
}

export default BurnRewards
